import { useState } from "react";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowRight, CalendarCheck, Phone, Mail, MapPin, Check, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useLeadDialog } from "@/components/LeadForms";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  COMPANY, SERVICES, PROCESS_STEPS, TRUST_BADGES, HERO_IMAGE, MONITORING_IMAGE,
} from "@/lib/constants";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08 } }),
};

export default function Home() {
  return (
    <div className="bg-[#050B18] min-h-screen" id="top">
      <Header />
      <Hero />
      <TrustBar />
      <Services />
      <WhyAtlas />
      <Process />
      <Contact />
      <Footer />
    </div>
  );
}

function Hero() {
  const { openQuote, openSurvey } = useLeadDialog();
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-20">
      <div className="absolute inset-0">
        <img src={HERO_IMAGE} alt="" className="w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050B18] via-[#050B18]/90 to-[#050B18]/40" />
        <div className="absolute inset-0 grid-overlay opacity-60" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 w-full">
        <motion.div initial="hidden" animate="show" variants={fadeUp} className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#38BDF8]/30 bg-[#0066FF]/10 px-4 py-1.5 mb-6">
            <ShieldCheck className="h-4 w-4 text-[#38BDF8]" />
            <span className="text-xs font-semibold tracking-[0.15em] text-[#38BDF8] uppercase">{COMPANY.serviceArea}</span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            <span className="text-white">Complete Protection.</span>
            <br />
            <span className="metallic-text">One Trusted Partner.</span>
          </h1>

          <p className="mt-6 text-lg sm:text-xl text-slate-300 max-w-2xl leading-relaxed">
            Atlas designs, installs, and monitors <span className="text-white font-semibold">CCTV, access control, intrusion
            detection, and fire &amp; life-safety systems</span> for commercial and residential properties across Middle Tennessee.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-4">
            <Button data-testid="hero-request-quote-btn" onClick={openQuote} size="lg" className="bg-[#0066FF] hover:bg-[#0052cc] text-white font-semibold h-13 px-7 text-base glow-blue group">
              Request a Quote
              <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Button>
            <Button data-testid="hero-book-survey-btn" onClick={openSurvey} size="lg" variant="outline" className="h-13 px-7 text-base border-slate-500 bg-transparent text-slate-100 hover:bg-slate-800 hover:text-white">
              <CalendarCheck className="mr-2 h-5 w-5" />
              Book a Free Site Survey
            </Button>
          </div>

          <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3">
            {TRUST_BADGES.map((b) => (
              <div key={b} className="flex items-center gap-2 text-sm text-slate-400">
                <Check className="h-4 w-4 text-[#38BDF8]" /> {b}
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function TrustBar() {
  return (
    <div className="border-y border-slate-800/80 bg-[#0A142F]/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-6 grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
        {[
          { n: "15+", l: "Years of Experience" },
          { n: "24/7", l: "Monitoring & Support" },
          { n: "100%", l: "Licensed & Insured" },
        ].map((s) => (
          <div key={s.l}>
            <div className="font-display text-2xl sm:text-3xl font-extrabold text-white">{s.n}</div>
            <div className="text-xs text-slate-400 mt-1 uppercase tracking-wide">{s.l}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Services() {
  const { openQuote } = useLeadDialog();
  return (
    <section id="services" className="py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="max-w-2xl mb-14">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#38BDF8] mb-3">What We Install</p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">Four systems. One trusted partner.</h2>
          <p className="mt-4 text-lg text-slate-400">Every Atlas solution is engineered, installed, and supported in-house — no sub-contractor handoffs.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {SERVICES.map((s, i) => {
            const Icon = Icons[s.icon] || ShieldCheck;
            return (
              <motion.div
                key={s.id}
                data-testid={`service-card-${s.id.replace("_", "-")}`}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                variants={fadeUp}
                className="card-hover group rounded-2xl border border-slate-700/80 bg-[#0A142F] overflow-hidden"
              >
                <div className="relative h-44 overflow-hidden">
                  <img src={s.image} alt={s.title} className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A142F] via-[#0A142F]/40 to-transparent" />
                  <span className="absolute top-4 left-4 flex h-11 w-11 items-center justify-center rounded-xl bg-[#050B18]/80 backdrop-blur text-[#38BDF8] ring-1 ring-[#38BDF8]/30">
                    <Icon className="h-5 w-5" />
                  </span>
                </div>
                <div className="p-6 sm:p-7">
                  <h3 className="font-display text-xl font-semibold text-white">{s.title}</h3>
                  <p className="text-sm text-[#38BDF8] mt-1 font-medium">{s.tagline}</p>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed">{s.description}</p>
                  <ul className="mt-5 grid grid-cols-2 gap-2">
                    {s.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm text-slate-300">
                        <Check className="h-4 w-4 text-[#0066FF] shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-5 text-xs text-slate-500"><span className="text-slate-400 font-medium">Typical use:</span> {s.useCases}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-12 text-center">
          <Button data-testid="services-quote-btn" onClick={openQuote} size="lg" className="bg-[#0066FF] hover:bg-[#0052cc] text-white font-semibold glow-blue group">
            Get a Tailored Quote <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Button>
        </div>
      </div>
    </section>
  );
}

function WhyAtlas() {
  return (
    <section id="why-atlas" className="py-20 lg:py-28 bg-[#0A142F]/40 border-y border-slate-800/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid lg:grid-cols-2 gap-14 items-center">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#38BDF8] mb-3">Why Atlas</p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">Security done right, from survey to monitoring.</h2>
          <p className="mt-4 text-lg text-slate-400 leading-relaxed">
            We're a local, licensed, and fully insured team that treats your property like our own. One accountable partner
            for design, installation, and ongoing support — so nothing falls through the cracks.
          </p>
          <div className="mt-8 grid sm:grid-cols-2 gap-5">
            {[
              { t: "Licensed & Insured", d: "State-certified technicians, fully bonded and insured for your peace of mind." },
              { t: "24/7 Monitoring", d: "Round-the-clock central station with immediate emergency dispatch." },
              { t: "Turnkey Installation", d: "We handle design, wiring, setup, and staff training — start to finish." },
              { t: "Local & Responsive", d: "Based in Middle Tennessee with fast on-site service when you need it." },
            ].map((item) => (
              <div key={item.t} className="rounded-xl border border-slate-700/80 bg-[#050B18] p-5">
                <div className="flex items-center gap-2 mb-2">
                  <ShieldCheck className="h-5 w-5 text-[#38BDF8]" />
                  <h3 className="font-display font-semibold text-white">{item.t}</h3>
                </div>
                <p className="text-sm text-slate-400">{item.d}</p>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="relative">
          <div className="rounded-2xl overflow-hidden border border-slate-700/80 glow-blue">
            <img src={MONITORING_IMAGE} alt="Atlas 24/7 monitoring center" className="w-full h-[460px] object-cover" />
          </div>
          <div className="absolute -bottom-6 -left-4 sm:left-6 glass rounded-xl border border-slate-700 px-6 py-4 flex items-center gap-3">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#10B981] opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-[#10B981]" />
            </span>
            <div>
              <div className="text-sm font-semibold text-white">Monitoring Active</div>
              <div className="text-xs text-slate-400">Central station online 24/7</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Process() {
  const { openSurvey } = useLeadDialog();
  return (
    <section id="process" className="py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="max-w-2xl mb-14">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#38BDF8] mb-3">How It Works</p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">From survey to monitoring in four steps.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {PROCESS_STEPS.map((p, i) => (
            <motion.div key={p.step} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="relative rounded-2xl border border-slate-700/80 bg-[#0A142F] p-7 card-hover">
              <div className="font-display text-5xl font-extrabold text-[#0066FF]/25">{p.step}</div>
              <h3 className="font-display text-lg font-semibold text-white mt-3">{p.name}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{p.desc}</p>
            </motion.div>
          ))}
        </div>
        <div className="mt-12 text-center">
          <Button data-testid="process-survey-btn" onClick={openSurvey} size="lg" variant="outline" className="border-slate-500 bg-transparent text-slate-100 hover:bg-slate-800 hover:text-white">
            <CalendarCheck className="mr-2 h-5 w-5" /> Book Your Free Survey
          </Button>
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll get back to you shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 lg:py-28 bg-[#0A142F]/40 border-t border-slate-800/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid lg:grid-cols-2 gap-14">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#38BDF8] mb-3">Get In Touch</p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">Let's protect what matters most.</h2>
          <p className="mt-4 text-lg text-slate-400 leading-relaxed">
            Call us, send an email, or drop a quick message. We respond fast and offer free, no-obligation site surveys across {COMPANY.serviceArea}.
          </p>

          <div className="mt-9 space-y-4">
            <a href={COMPANY.phoneHref} data-testid="contact-phone-link" className="flex items-center gap-4 rounded-xl border border-slate-700/80 bg-[#050B18] p-5 hover:border-[#38BDF8]/50 transition-colors group">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#0066FF]/15 text-[#38BDF8]"><Phone className="h-5 w-5" /></span>
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wide">Call Us</div>
                <div className="text-white font-semibold group-hover:text-[#38BDF8] transition-colors">{COMPANY.phone}</div>
              </div>
            </a>
            <a href={`mailto:${COMPANY.email}`} data-testid="contact-email-link" className="flex items-center gap-4 rounded-xl border border-slate-700/80 bg-[#050B18] p-5 hover:border-[#38BDF8]/50 transition-colors group">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#0066FF]/15 text-[#38BDF8]"><Mail className="h-5 w-5" /></span>
              <div className="min-w-0">
                <div className="text-xs text-slate-500 uppercase tracking-wide">Email Us</div>
                <div className="text-white font-semibold group-hover:text-[#38BDF8] transition-colors break-all">{COMPANY.email}</div>
              </div>
            </a>
            <div className="flex items-center gap-4 rounded-xl border border-slate-700/80 bg-[#050B18] p-5">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#0066FF]/15 text-[#38BDF8]"><MapPin className="h-5 w-5" /></span>
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wide">Service Area</div>
                <div className="text-white font-semibold">{COMPANY.serviceArea}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-700/80 bg-[#050B18] p-7 sm:p-9">
          <h3 className="font-display text-xl font-semibold text-white mb-5">Send us a message</h3>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-400 uppercase tracking-wide">Name *</Label>
              <Input data-testid="contact-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" className="bg-[#0A142F] border-slate-700" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-400 uppercase tracking-wide">Email *</Label>
                <Input data-testid="contact-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" className="bg-[#0A142F] border-slate-700" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-400 uppercase tracking-wide">Phone</Label>
                <Input data-testid="contact-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(931) 555-0199" className="bg-[#0A142F] border-slate-700" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-400 uppercase tracking-wide">Message *</Label>
              <Textarea data-testid="contact-message-input" value={form.message} onChange={(e) => set("message", e.target.value)} rows={4} placeholder="How can we help?" className="bg-[#0A142F] border-slate-700" />
            </div>
            <Button data-testid="contact-submit-btn" type="submit" disabled={submitting} className="w-full bg-[#0066FF] hover:bg-[#0052cc] text-white font-semibold h-11 glow-blue">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Message"}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
}
